import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Checkbox, Divider, Flex, Text } from '@/components/ui';
import { DataTable } from '@/components/data-table';
import { FilePreviewModal } from '@/components/file-preview';
import { CsvFile, CsvFileValidationStatus } from '@/types/csvFiles';
import { getCsvFileDownloadEndpoint } from '@/utils/downloadEndpoints';
import { getVersionBadgeText } from '@/utils/csvFileUtils';

import { IconRefresh, IconSearch, IconTrash, IconX } from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import CsvFileActions from './CsvFileActions';
import { CsvFileVersionsModal } from '@/components/file-versions';
import { CsvFilesEmptyState } from './CsvFilesEmptyState';
import { CsvFilesTableProps } from './types';

// Enable dayjs relative time plugin
dayjs.extend(relativeTime);

const CsvFilesTable: React.FC<CsvFilesTableProps> = ({
  files,
  loading = false,
  onDeleteFile,
  onBulkDelete,
  onRefresh,
  pagination,
  paginationState,
  onPaginationChange,
  actions,
  onUploadClick,
  isDeleting = false,
}) => {
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  const [selectedFileForVersions, setSelectedFileForVersions] =
    useState<CsvFile | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [fileToPreview, setFileToPreview] = useState<CsvFile | null>(null);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [filesState, setFilesState] = useState<CsvFile[]>(files);

  // Update local state when files prop changes
  React.useEffect(() => {
    setFilesState(files);
  }, [files]);

  const handleStatusChange = useCallback(
    (file: CsvFile, status: CsvFileValidationStatus) => {
      setFilesState(prev =>
        prev.map(f =>
          f.id === file.id ? { ...f, validation_status: status } : f
        )
      );
      // Trigger refresh to get updated data from backend (when implemented)
      onRefresh?.();
    },
    [onRefresh]
  );

  // Clear selection when files change (after deletion)
  React.useEffect(() => {
    // Clear selection for files that no longer exist
    const existingFileIds = new Set(files.map(f => f.id.toString()));
    setRowSelection(prev => {
      const newSelection: Record<string, boolean> = {};
      Object.keys(prev).forEach(fileId => {
        if (existingFileIds.has(fileId)) {
          newSelection[fileId] = prev[fileId];
        }
      });
      return newSelection;
    });
  }, [files]);

  // Note: All files in the list can be previewed/downloaded/versioned
  // Backend controls what files are returned in the list

  // Stable pagination change handler to avoid dependency loops
  const onPaginationChangeRef = useRef(onPaginationChange);
  useEffect(() => {
    onPaginationChangeRef.current = onPaginationChange;
  }, [onPaginationChange]);

  const paginationStateRef = useRef(paginationState);
  useEffect(() => {
    paginationStateRef.current = paginationState;
  }, [paginationState]);

  const handlePaginationChange = React.useCallback(
    (
      updater:
        | { pageIndex: number; pageSize: number }
        | ((prev: { pageIndex: number; pageSize: number }) => {
            pageIndex: number;
            pageSize: number;
          })
    ) => {
      if (typeof updater === 'function') {
        const currentPagination = {
          pageIndex: paginationStateRef.current?.pageIndex || 0,
          pageSize: paginationStateRef.current?.pageSize || 10,
        };
        const newPagination = updater(currentPagination);
        onPaginationChangeRef.current?.(newPagination);
      } else {
        onPaginationChangeRef.current?.(updater);
      }
    },
    [] // No dependencies to prevent loops
  );

  const handleShowVersions = React.useCallback((file: CsvFile) => {
    setSelectedFileForVersions(file);
    setVersionsModalOpen(true);
  }, []);

  const handleVersionsModalOpenChange = React.useCallback((open: boolean) => {
    setVersionsModalOpen(open);
    if (!open) {
      setTimeout(() => {
        setSelectedFileForVersions(null);
      }, 300);
    }
  }, []);

  const handlePreview = useCallback((file: CsvFile) => {
    setFileToPreview(file);
    setPreviewModalOpen(true);
  }, []);

  const handlePreviewModalChange = useCallback((open: boolean) => {
    setPreviewModalOpen(open);
    if (!open) {
      setFileToPreview(null);
    }
  }, []);

  // Debug: Log the generated API endpoint for preview
  const previewApiEndpoint = fileToPreview
    ? getCsvFileDownloadEndpoint(fileToPreview)
    : undefined;

  const enableColumnVisibility = true;
  const pageSizeOptions = useMemo(() => [10, 25, 50, 100], []);

  // Get selected files based on row selection
  const selectedFiles = useMemo(() => {
    return files.filter(file => rowSelection[file.id.toString()]);
  }, [files, rowSelection]);

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedFiles.length > 0 && onBulkDelete) {
      onBulkDelete(selectedFiles);
    }
  }, [selectedFiles, onBulkDelete]);

  const handleClearSelection = useCallback(() => {
    setRowSelection({});
  }, []);

  const columns: ColumnDef<CsvFile>[] = useMemo(
    () => [
      {
        id: 'select',
        header: () => {
          const isAllSelected =
            files.length > 0 &&
            files.every(file => rowSelection[file.id.toString()]);
          return (
            <Checkbox
              checked={isAllSelected}
              onCheckedChange={checked => {
                const newSelection: Record<string, boolean> = {};
                if (checked) {
                  files.forEach(file => {
                    newSelection[file.id.toString()] = true;
                  });
                }
                setRowSelection(newSelection);
              }}
              size="small"
            />
          );
        },
        cell: ({ row }) => {
          const fileId = row.original.id.toString();
          return (
            <Checkbox
              checked={!!rowSelection[fileId]}
              onCheckedChange={checked => {
                setRowSelection(prev => ({
                  ...prev,
                  [fileId]: checked === true,
                }));
              }}
              size="small"
            />
          );
        },
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
        meta: {
          size: '40px',
          systemColumn: true,
        },
      },
      {
        accessorKey: 'id',
        header: 'ID',
        meta: {
          cell: {
            variant: 'id',
          },
          size: 80,
        },
      },
      {
        accessorKey: 'filename',
        header: 'File Name',
        filterFn: 'includesString',
        meta: {
          cell: {
            variant: 'fileName',
            showIcon: true,
            endSlot: (row: CsvFile) => (
              <Badge size="xsmall" color="feature" variant="surface">
                {getVersionBadgeText(row)}
              </Badge>
            ),
          },
          required: true,

          minSize: '300px',
          maxSize: '1fr',
        },
      },
      {
        accessorKey: 'extension',
        header: 'Extension',
        enableSorting: false,
        meta: {
          cell: {
            variant: 'fileFormat',
          },
          size: '140px',
        },
      },
      {
        accessorKey: 'size',
        header: 'Size',
        meta: {
          cell: {
            variant: 'fileSize',
          },
          size: '140px',
        },
      },
      {
        accessorKey: 'content_type',
        header: 'Type',
        filterFn: 'includesString',
        meta: {
          cell: {
            variant: 'mimeType',
          },
          size: '140px',
          defaultVisible: false,
        },
      },
      {
        id: 'uploaded_by',
        accessorFn: (row: CsvFile) => row.last_version?.created_by || row.user,
        header: 'Uploaded by',
        enableSorting: false,
        meta: {
          cell: {
            variant: 'user',
            showEmail: true,
          },
          size: '200px',
        },
      },
      {
        id: 'uploaded_at',
        accessorFn: (row: CsvFile) => row.last_version?.created_at || row.created_at,
        header: 'Uploaded',
        meta: {
          cell: {
            variant: 'datetime',
            relative: true,
            truncate: true,
          },
          size: '180px',
        },
      },
      {
        accessorKey: 'validation_status',
        header: 'Validation Status',
        meta: {
          cell: {
            variant: 'validationStatus',
            onStatusChange: handleStatusChange,
          },
          size: '200px',
          minSize: 180,
          maxSize: 250,
        },
      },
      {
        id: 'last_imported_version',
        accessorFn: (row: CsvFile) => row.last_imported_version,
        header: 'Imported Version',
        meta: {
          cell: {
            variant: 'versionInfo',
          },
          size: '180px',
          minSize: 150,
          maxSize: 220,
        },
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <CsvFileActions
            file={row.original}
            onDelete={onDeleteFile}
            onShowVersions={handleShowVersions}
            onPreview={handlePreview}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
        meta: {
          size: 'auto',
          // minSize: 'auto',
          systemColumn: true,
        },
      },
    ],
    [
      onDeleteFile,
      handleShowVersions,
      handlePreview,
      rowSelection,
      files,
      handleStatusChange,
    ]
  );

  const bulkActionSlot = useMemo(() => {
    if (selectedFiles.length === 0) return null;

    return (
      <Flex gap="2" align="center">
        <Text size="2" color="gray">
          {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}{' '}
          selected
        </Text>
        <Button
          variant="surface"
          size="small"
          color="error"
          startIcon={<IconTrash />}
          onClick={handleBulkDeleteClick}
          disabled={isDeleting || loading || !onBulkDelete}
          title={!onBulkDelete ? 'You do not have permission to delete files' : undefined}
        >
          Delete Selected
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<IconX />}
          onClick={handleClearSelection}
          disabled={isDeleting || loading}
          title="Clear selection"
        >
          Clear selection
        </Button>
        <Divider orientation="vertical" size="small" />
      </Flex>
    );
  }, [
    selectedFiles.length,
    handleBulkDeleteClick,
    handleClearSelection,
    isDeleting,
    loading,
    onBulkDelete,
  ]);

  const rightSlot = useMemo(
    () => (
      <>
        <Button
          variant="outlined"
          size="small"
          onClick={onRefresh}
          startIcon={<IconRefresh />}
          disabled={loading || isDeleting}
        >
          Refresh
        </Button>
        {actions && (
          <>
            <Divider orientation="vertical" size="small" />
            {actions}
          </>
        )}
      </>
    ),
    [onRefresh, loading, actions, isDeleting]
  );

  // Memoize pagination config with stable handler reference
  const paginationConfig = useMemo(
    () => ({
      pageSize: paginationState?.pageSize || 10,
      pageIndex: paginationState?.pageIndex || 0,
      pageCount: pagination?.total_pages || 0,
      totalCount: pagination?.total_count || 0,
      onPaginationChange: handlePaginationChange,
    }),
    [
      paginationState?.pageSize,
      paginationState?.pageIndex,
      pagination?.total_pages,
      pagination?.total_count,
      handlePaginationChange,
    ]
  );

  // Custom empty content for when no files exist
  const customEmptyContent = onUploadClick ? (
    <CsvFilesEmptyState onUploadClick={onUploadClick} />
  ) : undefined;

  return (
    <>
      <DataTable
        data={filesState}
        columns={columns}
        enableSorting={true}
        enableFiltering={true}
        enablePagination={true}
        manualPagination={true}
        enableColumnVisibility={enableColumnVisibility}
        enableRowSelection={!!onBulkDelete}
        rowSelection={{
          state: rowSelection,
          onRowSelectionChange: setRowSelection,
        }}
        loading={loading}
        searchPlaceholder="Search CSV files..."
        pageSizeOptions={pageSizeOptions}
        toolbarLeftSlot={bulkActionSlot}
        toolbarRightSlot={rightSlot}
        emptyMessage="No CSV files found"
        emptyIcon={<IconSearch />}
        customEmptyContent={customEmptyContent}
        getRowId={(row: CsvFile) => row.id.toString()}
        pagination={paginationConfig}
      />

      {/* Versions Modal */}
      {selectedFileForVersions && (
        <CsvFileVersionsModal
          file={selectedFileForVersions}
          open={versionsModalOpen}
          onOpenChange={handleVersionsModalOpenChange}
        />
      )}

      {/* File Preview Modal */}
      <FilePreviewModal
        key={`preview-${fileToPreview?.id || 'none'}`}
        apiEndpoint={previewApiEndpoint}
        filename={fileToPreview?.filename || ''}
        mimeType={fileToPreview?.content_type}
        fileSize={fileToPreview?.size}
        open={previewModalOpen && !!fileToPreview}
        onOpenChange={handlePreviewModalChange}
        title={fileToPreview ? `Preview: ${fileToPreview.filename}` : 'Preview'}
        hasPreviewPermission={true}
      />
    </>
  );
};

export default CsvFilesTable;
